import { useCallback, useEffect, useRef } from 'react';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { useJitteredPoll } from '../../../shared/hooks/useJitteredPoll';
import {
  auditQuestionResponsesKey,
  fetchAuditQuestionResponses,
} from './useAuditQuestionResponsesQuery';
import {
  mergePolledResponsesIntoSummaryCache,
  type AuditQuestionItemResponse,
} from '../logic';

/**
 * Base cadence — matches V1's `useQuestionResponsePoller` so
 * multiplexing V1 + V2 clients against the same backend produces the
 * same load profile per client. Jitter is added on top per tick to
 * avoid coordinated bursts.
 */
const POLL_INTERVAL_MS = 15_000;
const POLL_JITTER_MS = 3_000;

export interface UseAuditQuestionResponsePollingOptions {
  /**
   * BU-id. Nullable/undefined => loop stays parked (matches the
   * page-level gate on the audit summary query itself).
   */
  readonly businessUnitId: string | null | undefined;
  /**
   * Audit-id. Empty string => loop stays parked.
   */
  readonly auditId: string;
  /**
   * When false, the loop stays parked regardless of ids. Callers
   * use this for route matches / page-level enable flags.
   */
  readonly enabled: boolean;
}

/**
 * Poll the audit-response list endpoint on a jittered cadence and
 * merge results into every matching `useAuditSummaryQuery` cache.
 * Cross-tab sync for parent + sub answers, comments, media, tasks
 * (per the Option-2 endpoint choice — see the polling design doc).
 *
 * Guarded pauses:
 *   1. `enabled` — caller gate (audit id present, page focus, etc.)
 *   2. `document.hidden` — handled by `useJitteredPoll` internally
 *   3. `isMutating()` returning true — either at tick start OR at
 *      poll completion. The double check catches the case where a
 *      mutation fires while a poll's fetch is in flight; without
 *      the completion-side check, the merger could overwrite the
 *      optimistic patch. See design constraint on the async-
 *      interleaving matrix.
 */
export function useAuditQuestionResponsePolling(
  options: UseAuditQuestionResponsePollingOptions,
): void {
  const { businessUnitId: rawBusinessUnitId, auditId, enabled } = options;
  const businessUnitId = rawBusinessUnitId ?? '';
  const canPoll = enabled && !!businessUnitId && !!auditId;
  const queryClient = useQueryClient();

  // Global in-flight mutation count from React Query. Any pending
  // mutation on the page (parent answer, sub answer, bulk answer,
  // comment, NA reason, media, task draft — every save path
  // registers here) causes the poll to skip and drops any
  // in-flight fetch on completion. Ref-mirrored so the timer
  // callback reads the latest count without restarting the loop.
  const inFlightMutationCount = useIsMutating();
  const inFlightMutationCountRef = useRef(inFlightMutationCount);
  inFlightMutationCountRef.current = inFlightMutationCount;

  const onPoll = useCallback(async (): Promise<void> => {
    if (!businessUnitId || !auditId) return;
    // Drive the SAME cache entry `useAuditQuestionResponsesQuery`
    // subscribes to, rather than issuing a parallel request. This hook
    // owns the SCHEDULE (interval + jitter + tab-visibility + the
    // mutation pause below); React Query owns the fetch, dedupes
    // concurrent callers, and notifies the page's subscriber when the
    // entry updates. One network call per tick, one copy of the truth.
    const input = { businessUnitId, auditId };
    const response = await queryClient.fetchQuery({
      queryKey: auditQuestionResponsesKey(input),
      queryFn: () => fetchAuditQuestionResponses(input),
      // Always go to the wire on a scheduled tick — the whole point of
      // the poll is to pick up other users' writes.
      staleTime: 0,
    });
    // Post-settle intent re-check — the mutation may have fired
    // during our fetch window. Dropping the result is safer than
    // clobbering the fresh optimistic patch the mutation wrote,
    // and the next poll (15s from now) will re-fetch and reconcile.
    if (inFlightMutationCountRef.current > 0) return;
    const items = (response.auditQuestionResponses ??
      []) as unknown as readonly AuditQuestionItemResponse[];
    mergePolledResponsesIntoSummaryCache(queryClient, auditId, items);
  }, [queryClient, businessUnitId, auditId]);

  const isPaused = useCallback((): boolean => inFlightMutationCountRef.current > 0, []);

  // Fire an immediate poll on mount (and whenever the target audit
  // changes) so the response-token sidecar is populated before the
  // auditor's first save. Without this, the first ~15s after page
  // load has no `expectedLastUpdatedAt` on write requests — matching
  // desktop V1's behaviour where `pollQuestionResponsesApiThunk`
  // dispatches once on mount alongside the interval poller.
  //
  // Uses onPoll via ref so this effect isn't tied to onPoll's
  // referential identity — same pattern useJitteredPoll uses
  // internally to avoid restart churn.
  const onPollRef = useRef(onPoll);
  onPollRef.current = onPoll;
  useEffect(() => {
    if (!canPoll) return;
    // Same mutation-in-flight gate the interval poller uses:
    // don't waste a request or clobber an optimistic patch when a
    // save is already running against the audit.
    if (inFlightMutationCountRef.current > 0) return;
    void onPollRef.current();
    // Depends on the audit identity — swapping audits (e.g. cursor
    // jump between adjacent audits) must re-seed the sidecar for
    // the new target immediately.
  }, [canPoll, businessUnitId, auditId]);

  useJitteredPoll({
    intervalMs: POLL_INTERVAL_MS,
    jitterMs: POLL_JITTER_MS,
    enabled: canPoll,
    onPoll,
    isPaused,
  });
}
